import { NavLink, useHistory, useParams } from "react-router-dom";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import {
  useDdqlQuery,
  Question as GqlQuestion,
  DdqlStatus,
  Ddql as GqlDdql,
  usePublishDdqlMutation,
  useDeleteDdqlMutation,
  DdqlQuery,
  useUpdateDdqlMutation,
  useDealActivityQuery,
  DealActivityType,
  QuestionStatus,
  Account,
  PermissionsResourceType,
  DealRoleType,
  AccessControlDealGroup,
  useUpdateQuestionsMutation,
  QuestionPriority,
  useDeleteQuestionsMutation,
  useCreateDdqlImportMutation,
  S3DdqlUploadCompleteDocument,
  S3DdqlUploadCompleteMutation,
  S3DdqlUploadCompleteMutationVariables,
} from "../../../graphql/generated";
import { classNames } from "../../../utils/cn";
import { Button, ButtonPopout } from "../../../components/tailwind/Button";
import {
  formatDistance,
  getUnixTime,
  secondsToMilliseconds,
  startOfDay,
  sub,
} from "date-fns";
import { NewDDQLModal } from "./../NewDDQLModal";
import { toasts } from "../../../components/toasts/toasts";
import {
  PlusIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  ChatBubbleLeftIcon,
  FolderIcon,
} from "@heroicons/react/24/solid";
import { ListBulletIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import {
  ActivityIcon,
  ArrowUpRightIcon,
  BadgeQuestionMarkIcon,
  FileUpIcon,
  ImportIcon,
  PanelRightCloseIcon,
  PanelRightIcon,
  Trash2Icon,
  TrashIcon,
} from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import useGqlClient from "@/src/hooks/useGqlClient";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { Question, QuestionsFilters } from "..";
import { NewQuestionModal } from "../NewQuestionModal";
import { DeleteDDQLModal } from "./DeleteDDQLModal";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { MoveQuestionToDDQLModal } from "./MoveQuestionToDDQLModal";
import { Menu } from "@headlessui/react";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import {
  Breadcrumb,
  Breadcrumbs,
} from "@/src/components/breadcrumbs/Breadcrumbs";
import { links } from "@/src/utils/links";
import { BackButton } from "@/src/components/BackButton";
import { ProgressBar } from "@/src/components/ProgressBar";
import { Avatar } from "@/src/components/account/Avatar";
import { ListPlusIcon } from "lucide-react";
import {
  FilterButton,
  FilterOption,
} from "@/src/components/data_room/filters/filter_button";
import { DdqlRow } from "@/src/components/DdqlRow";
import { Uploader, getNumberOfParts } from "@/src/utils/multipartUploader";
import { AddMoreModal } from "@/src/components/access_control/AddMoreModal";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { LinkDataRoomFolderModal } from "./LinkDataRoomFolderModal";
import { Avatars } from "@/src/components/Avatars";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { Checkbox } from "@/src/components/checkbox";
import clsx from "clsx";
import {
  getQuestionPriorityText,
  QuestionPriorityIcon,
} from "@/src/components/QuestionPriorityIcon";
import { truncateText } from "@/src/utils/truncateText";
import { LocalFileIcon } from "@/src/components/icons/LocalFileIcon";
import { PreviewDdqlModal } from "./PreviewDdqlImportModal";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import { SlideOver } from "@/src/components/SlideOver";
import { ErrorBoundary } from "@sentry/react";
import { SpreadSheet } from "../../data_room/data_room_file/FileContent";
const DDQL_ACTIVITY_TYPES = [
  DealActivityType.QuestionAnswered,
  DealActivityType.QuestionAsked,
  DealActivityType.Comment,
  DealActivityType.DdqlCreated,
  DealActivityType.DdqlImportStatusFailed,
  DealActivityType.DdqlImportStatusSuccess,
  DealActivityType.DdqlPublished,
  DealActivityType.DdqlPermissionChanged,
];

export interface DdqlContextType {
  filters: QuestionsFilters;
  setFilters: (filters: QuestionsFilters) => void;
  selectedQuestions: string[];
  setSelectedQuestions: (questions: string[]) => void;
  showSourceFile: boolean;
  setShowSourceFile: (show: boolean) => void;
}

export const DdqlContext = createContext<DdqlContextType>({
  filters: {},
  setFilters: (filters: QuestionsFilters) => { },
  selectedQuestions: [],
  setSelectedQuestions: (questions: string[]) => { },
  showSourceFile: false,
  setShowSourceFile: (show) => { },
});

const lastUpdatedFilterOptions: { value: number; label: string }[] = [
  {
    label: "Today",
    value: getUnixTime(startOfDay(new Date())),
  },
  {
    label: "Last 3 days",
    value: getUnixTime(sub(new Date(), { days: 3 })),
  },
  {
    label: "Last 7 days",
    value: getUnixTime(sub(new Date(), { days: 7 })),
  },
  {
    label: "Last 30 days",
    value: getUnixTime(sub(new Date(), { days: 30 })),
  },
];

const ACTIVITY_PAGE_SIZE = 15;

function DdqlBreadcrumbs({ ddql }: { ddql: DdqlQuery["ddql"] }) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  let nodes: Breadcrumb[] = [
    { link: `/deal/${activeDealId}/questions`, text: "Questions" },
  ];

  if (ddql.parentDdqls.length > 0) {
    ddql.parentDdqls.forEach((parentDdql) => {
      if (parentDdql.name === "") {
        return;
      }
      nodes.push({
        link: links.ddqlLink(parentDdql, activeDealId),
        text: parentDdql.name,
      });
    });
  }
  return <Breadcrumbs breadcrumbs={nodes} pageTitle={ddql.name} />;
}

const refetchStatuses = [
  DdqlStatus.Importing,
  DdqlStatus.Uploading,
  DdqlStatus.Initial,
  DdqlStatus.Published,
];

export function Ddql() {
  const { id } = useParams<{ id: string }>();

  const history = useHistory();
  const [openModal, setOpenModal] = useState<
    | ""
    | "import_ddql"
    | "new_question"
    | "create_ddql"
    | "assign_to_deal_group"
    | "confirm_delete"
    | "move_questions"
    | "set_questions_priority"
    | "delete_questions"
  >("");

  const [moveQuestionState, setMoveQuestionState] = useState<{
    open: boolean;
    question: GqlQuestion | null;
    targetDdqlId: string | null;
    targetDdqlName: string | null;
  }>({
    open: false,
    question: null,
    targetDdqlId: null,
    targetDdqlName: null,
  });

  const [currentlyDraggedQuestionDdqlId, setCurrentlyDraggedQuestionDdqlId] =
    useState<string | null>(null);

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const deleteQuestions = useDeleteQuestionsMutation(client);

  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [filters, setFilters] = useState<QuestionsFilters>({});
  const [showActivity, setShowActivity] = useState(false);
  const [showSourceFile, setShowSourceFile] = useState(false);

  useEffect(() => {
    setShowSourceFile(false);
  }, [id]);

  const ddqlQuery = useDdqlQuery(
    client,
    {
      id,
      dealId: activeDealId ?? "",
    },
    {
      enabled: id !== "" && activeDealId !== "",
      refetchInterval(query) {
        if (
          query.state.data &&
          query.state.data.ddql.status &&
          refetchStatuses.includes(query.state.data.ddql.status)
        ) {
          if (query.state.data.ddql.status === DdqlStatus.Published) {
            if (
              query.state.data.ddql.children.length +
              query.state.data.ddql.totalChildQuestions ===
              0
            ) {
              return 1000;
            }

            return false;
          }

          return 1000;
        }
        return false;
      },
    },
  );

  const [createdByOptions, setCreatedByOptions] = useState<
    FilterOption<Pick<Account, "id" | "name" | "avatar">>[]
  >([]);

  function getDdqlCreatedBys(ddql: DdqlQuery["ddql"]): Account[] {
    let createdBys = [ddql.createdBy];
    for (const q of ddql.questions) {
      createdBys.push(q.createdBy);
    }

    for (const child of ddql.children) {
      const childCreatedBys = getDdqlCreatedBys(child as GqlDdql);
      createdBys.push(...childCreatedBys);
    }

    return createdBys as Account[];
  }

  useEffect(() => {
    if (!ddqlQuery.data) {
      return;
    }

    const questionsCreatedBys = ddqlQuery.data.ddql.questions.map(
      (q) => q.createdBy,
    );

    let ddqlCreatedBys = [];
    for (const ddql of ddqlQuery.data.ddql.children) {
      const cbs = getDdqlCreatedBys(ddql as GqlDdql);
      ddqlCreatedBys.push(...cbs);
    }

    const uniqueCreatedBys = [...questionsCreatedBys, ...ddqlCreatedBys]
      .filter(
        (createdBy, index, self) =>
          index === self.findIndex((t) => t.id === createdBy.id),
      )
      .map((createdBy) => ({
        value: createdBy,
        label: createdBy.name,
      }));

    setCreatedByOptions(uniqueCreatedBys);
  }, [ddqlQuery.data]);

  const publishDdql = usePublishDdqlMutation(client);
  const deleteDdql = useDeleteDdqlMutation(client);

  // const onDragStart = (start: any) => {
  //   if (!canDragAndDrop) return;

  //   const sourceDdqlId = start.source.droppableId.replace("ddql-", "");

  //   setCurrentlyDraggedQuestionDdqlId(sourceDdqlId);
  // };

  // const onDragEnd = (result: DropResult) => {
  //   if (!canDragAndDrop) return;

  //   setCurrentlyDraggedQuestionDdqlId(null);

  //   const { destination, source, draggableId } = result;

  //   if (!destination) return;
  //   if (
  //     destination.droppableId === source.droppableId &&
  //     destination.index === source.index
  //   )
  //     return;

  //   const targetDdqlId = destination.droppableId.replace("ddql-", "");

  //   const sourceDdqlId = source.droppableId.replace("ddql-", "");

  //   if (sourceDdqlId === targetDdqlId) return;

  //   const findDdql = (ddqlId: string, ddqls: any[]): any => {
  //     for (const ddql of ddqls) {
  //       if (ddql.id === ddqlId) return ddql;
  //       if (ddql.children && ddql.children.length > 0) {
  //         const found = findDdql(ddqlId, ddql.children);
  //         if (found) return found;
  //       }
  //     }
  //     return null;
  //   };

  //   let targetDdql = findDdql(
  //     targetDdqlId,
  //     ddqlQuery.data?.ddql.children || [],
  //   );

  //   if (!targetDdql && ddqlQuery.data?.ddql.id === targetDdqlId) {
  //     targetDdql = ddqlQuery.data.ddql;
  //   }

  //   if (!targetDdql) return;

  //   if (targetDdql.children && targetDdql.children.length > 0) {
  //     return;
  //   }

  //   const questionId = draggableId.replace("question-", "");
  //   const question = findQuestion(questionId, ddqlQuery.data?.ddql);

  //   if (!question) return;

  //   setMoveQuestionState({
  //     open: true,
  //     question,
  //     targetDdqlId,
  //     targetDdqlName: targetDdql.name || "Unnamed List",
  //   });
  // };

  const findQuestion = (questionId: string, ddql: any): any => {
    if (!ddql) return null;

    const question = ddql.questions?.find((q: any) => q.id === questionId);
    if (question) return question;

    if (ddql.children) {
      for (const child of ddql.children) {
        const found = findQuestion(questionId, child);
        if (found) return found;
      }
    }

    return null;
  };

  if (ddqlQuery.isLoading || !ddqlQuery.data) {
    return (
      <div className="flex-1 flex-col flex">
        <div className="bg-white sticky right-0 z-1 top-0 px-2 py-3 w-full border-y border-concrete-100">
          <div className="flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-x-3">
              <div className="h-6 w-6 bg-gray-200 rounded" />
              <div className="h-5 w-40 bg-gray-200 rounded" />
              <div className="h-6 w-48 bg-gray-100 rounded-full" />
              <div className="h-6 w-56 bg-gray-100 rounded-full" />
            </div>
            <div className="flex items-center gap-x-2">
              <div className="w-32 h-1 bg-gray-200 rounded-full" />
              <div className="h-4 w-12 bg-gray-200 rounded" />
              <div className="h-6 w-6 bg-gray-200 rounded" />
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between gap-x-3 mt-2 px-2 animate-pulse">
            <div className="h-8 w-28 bg-gray-200 rounded" />
            <div className="flex items-center gap-x-2 py-1">
              <div className="h-8 w-28 bg-gray-200 rounded" />
              <div className="h-8 w-32 bg-gray-200 rounded" />
            </div>
          </div>

          <div className="mt-2 animate-pulse">
            <table className="min-w-full divide-y divide-concrete-100">
              <thead>
                <tr>
                  <th className="px-3 py-2"><div className="h-4 w-4 bg-gray-200 rounded" /></th>
                  <th className="px-4 py-2 text-left" style={{ width: "100%" }}><div className="h-3 w-16 bg-gray-200 rounded" /></th>
                  <th className="px-4 py-2"><div className="h-3 w-20 bg-gray-200 rounded" /></th>
                  <th className="px-4 py-2"><div className="h-3 w-14 bg-gray-200 rounded" /></th>
                  <th className="px-4 py-2"><div className="h-3 w-14 bg-gray-200 rounded" /></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-concrete-100">
                {[...Array(12)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-3 py-3"><div className="h-4 w-4 bg-gray-200 rounded" /></td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded" style={{ width: `${55 + (i * 17) % 35}%` }} />
                    </td>
                    <td className="px-4 py-3"><div className="flex items-center gap-x-2"><div className="h-6 w-6 bg-gray-200 rounded-full" /><div className="h-3 w-20 bg-gray-200 rounded" /></div></td>
                    <td className="px-4 py-3"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-3 w-16 bg-gray-200 rounded" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const questions = ddqlQuery.data.ddql.questions;
  const children = ddqlQuery.data.ddql.children;

  const showNoQuestionsMessage =
    questions?.length === 0 && !(children && children.length > 0);

  if (ddqlQuery.error) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center h-full w-full">
        <p className="font-semibold text-gray-600 text-sm">
          Something went wrong
        </p>
        <p className="text-gray-500/80 text-xs">
          Sorry about that. Try again or contact support.
        </p>
        <Button
          margin="m 0 0 0"
          variant="neutral"
          text="Try again"
          onClick={() => {
            ddqlQuery.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <DdqlContext.Provider
      value={{
        selectedQuestions,
        setSelectedQuestions,
        filters,
        setFilters,
        showSourceFile,
        setShowSourceFile,
      }}
    >
      <div className="flex-1 flex-col flex">
        <div className="bg-white sticky right-0 z-1 top-0 px-2 py-3 w-full border-y border-concrete-100">
          <div className="flex items-center justify-between">
            <Header ddql={ddqlQuery.data.ddql} />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row  lg:divide-x lg:divide-concrete-100">
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between gap-x-3 mt-2 px-2">
              <div className="flex items-center gap-x-2">
                <FilterButton
                  defaultLabel="Created by"
                  options={createdByOptions}
                  selected={createdByOptions.find(
                    (option) => option.value === filters.createdBy,
                  )}
                  onChange={(value) => {
                    setFilters({
                      ...filters,
                      createdBy: value as Pick<
                        Account,
                        "id" | "name" | "avatar"
                      >,
                    });
                  }}
                  renderOption={(option) => {
                    return (
                      <div className="flex items-center gap-x-2">
                        <Avatar account={option.value} size="xs" />
                        <p className="text-sm font-semibold text-gray-500">
                          {option.label}
                        </p>
                      </div>
                    );
                  }}
                />
                {/* <FilterButton
                defaultLabel="Question list"
                options={lastUpdatedFilterOptions}
                selected={lastUpdatedFilterOptions.find(
                  (option) => option.value === filters.lastUpdated,
                )}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    lastUpdated: value as number,
                  });
                }}
                renderOption={(option) => {
                  return (
                    <div className="flex items-center gap-x-2">
                      <p className="text-sm font-semibold text-gray-500">
                        {option.label}
                      </p>
                    </div>
                  );
                }}
              />
              <FilterButton
                defaultLabel="Last updated"
                options={lastUpdatedFilterOptions}
                selected={lastUpdatedFilterOptions.find(
                  (option) => option.value === filters.lastUpdated,
                )}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    lastUpdated: value as number,
                  });
                }}
                renderOption={(option) => {
                  return (
                    <div className="flex items-center gap-x-2">
                      <p className="text-sm font-semibold text-gray-500">
                        {option.label}
                      </p>
                    </div>
                  );
                }}
              /> */}
              </div>
              <div className="flex items-center justify-between gap-x-3">
                <div className="flex items-center gap-x-2">
                  <PermissionCheck
                    input={{
                      permissionKey: "questions:edit",
                      resource: {
                        resourceID: ddqlQuery.data.ddql.id,
                        resourceType: PermissionsResourceType.Ddql,
                      },
                    }}
                  >
                    <div className="flex gap-x-2">
                      {selectedQuestions.length === 0 ? (
                        <div className="flex gap-x-2 py-1">
                          <Button
                            icon={ListPlusIcon}
                            variant="neutral"
                            size="s"
                            text="New section"
                            onClick={() => {
                              setOpenModal("create_ddql");
                            }}
                          />
                          <Button
                            size="s"
                            variant="positive"
                            icon={PlusIcon}
                            text="New question"
                            onClick={() => {
                              setOpenModal("new_question");
                            }}
                          />
                          {!showActivity && (
                            <Button
                              onClick={() => setShowActivity(true)}
                              variant="neutral"
                              icon={ActivityIcon}
                              text=""
                              size="s"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-x-2 box-content py-1 bg-concrete-100 px-1.5 rounded-md">
                          <div className="flex items-center space-x-2">
                            <CloseIcon
                              onClose={() => {
                                setSelectedQuestions([]);
                              }}
                            />
                            <span className="hidden md:flex text-sm font-semibold text-gray-600">
                              {selectedQuestions.length} selected
                            </span>
                          </div>
                          <Button
                            id="move-questions"
                            size="s"
                            variant="secondary"
                            icon={ArrowUpRightIcon}
                            text="Move to"
                            onClick={() => {
                              if (openModal === "move_questions") {
                                setOpenModal("");
                              } else {
                                setOpenModal("move_questions");
                              }
                            }}
                          />
                          <ButtonPopout
                            buttonElementId="move-questions"
                            open={openModal === "move_questions"}
                            onClose={() => setOpenModal("")}
                            className="w-72"
                          >
                            <div className="">
                              <p className="p-2 text-persian-990 font-semibold text-xs">
                                Move questions to different section
                              </p>
                              <MoveQuestions
                                ddql={ddqlQuery.data.ddql}
                                onComplete={() => {
                                  setOpenModal("");
                                }}
                              />
                            </div>
                          </ButtonPopout>

                          <Button
                            id="set-questions-priority"
                            size="s"
                            variant="secondary"
                            icon={ChartBarIcon}
                            text="Set priority"
                            onClick={() => {
                              setOpenModal("set_questions_priority");
                            }}
                          />
                          <ButtonPopout
                            buttonElementId="set-questions-priority"
                            open={openModal === "set_questions_priority"}
                            onClose={() => setOpenModal("")}
                            className="w-72"
                          >
                            <div className="">
                              <p className="p-2 text-persian-990 font-semibold text-xs">
                                Set priority
                              </p>
                              <SetQuestionsPriority
                                ddql={ddqlQuery.data.ddql}
                                onComplete={() => {
                                  setOpenModal("");
                                }}
                              />
                            </div>
                          </ButtonPopout>
                          <Button
                            id="delete-questions"
                            size="s"
                            variant="danger"
                            icon={Trash2Icon}
                            text="Delete"
                            onClick={() => {
                              setOpenModal("delete_questions");
                            }}
                          />
                          <ButtonPopout
                            buttonElementId="delete-questions"
                            open={openModal === "delete_questions"}
                            onClose={() => setOpenModal("")}
                            className="w-72"
                          >
                            <div className="p-2">
                              <p className="text-persian-990 font-semibold text-xs">
                                Confirm delete questions
                              </p>
                              <p className="text-xs text-concrete-600">
                                Are you sure you want to delete these questions?
                              </p>

                              <Button
                                margin="m 0 0 "
                                variant="danger"
                                size="s"
                                icon={TrashIcon}
                                text="Yes, delete"
                                isLoading={deleteQuestions.isPending}
                                onClick={() => {
                                  if (deleteQuestions.isPending) {
                                    return;
                                  }

                                  deleteQuestions.mutate(
                                    {
                                      input: selectedQuestions.map((q) => {
                                        return {
                                          id: q,
                                        };
                                      }),
                                    },
                                    {
                                      onSuccess: () => {
                                        toasts.success("Questions deleted");
                                        setOpenModal("");
                                        setSelectedQuestions([]);
                                        queryClient.invalidateQueries({
                                          queryKey: [
                                            "Ddql",
                                            { id: ddqlQuery.data.ddql.id },
                                          ],
                                        });
                                      },
                                    },
                                  );
                                }}
                              />
                            </div>
                          </ButtonPopout>
                        </div>
                      )}
                    </div>
                  </PermissionCheck>
                </div>
              </div>
            </div>

            <div className="max-h-[calc(100vh-220px)] overflow-y-scroll scrollbar-thin mt-2 rounded-b-md">
              {ddqlQuery.data.ddql.totalChildQuestions +
                ddqlQuery.data.ddql.children.length >
                0 ? (
                <div className="flex">
                  {showSourceFile ? (
                    <div
                      key={ddqlQuery.data.ddql.sourceFilePresignedUrl}
                      className="flex-1  overflow-y-scroll no-scrollbar"
                    >
                      <ErrorBoundary
                        fallback={
                          <div className="w-full h-full flex items-center justify-center">
                            <p className="font-semibold text-sm text-concrete-600">
                              Failed to load file
                            </p>
                          </div>
                        }
                      >
                        <SpreadSheet
                          url={ddqlQuery.data.ddql.sourceFilePresignedUrl}
                        />
                      </ErrorBoundary>
                    </div>
                  ) : null}

                  <div className="flex-1 max-h-[calc(100vh-220px)] overflow-y-scroll scrollbar-none">
                    <DdqlTable ddql={ddqlQuery.data.ddql} filters={filters} />
                  </div>
                </div>
              ) : (
                <div>
                  <EmptyState
                    ddql={ddqlQuery.data.ddql}
                    dealId={activeDealId}
                  />
                </div>
              )}
            </div>
          </div>
          {showActivity && (
            <PermissionCheck
              input={{
                permissionKey: "questions:view",
                roleType: DealRoleType.Internal,
              }}
            >
              <DdqlActivity
                ddqlId={id}
                dealId={activeDealId ?? ""}
                onClose={() => setShowActivity(false)}
              />
            </PermissionCheck>
          )}
        </div>
        <NewQuestionModal
          open={openModal === "new_question"}
          onClose={() => {
            setOpenModal("");
            // Ensure we refresh the current DDQL
            if (activeDealId) {
              queryClient.invalidateQueries({
                queryKey: ["Ddql", { id, dealId: activeDealId }],
              });
            }
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id }],
            });
          }}
          ddqlId={id}
        />
        <NewDDQLModal
          open={openModal === "create_ddql"}
          onClose={() => {
            setOpenModal("");
            queryClient.invalidateQueries({
              queryKey: ["Ddql"],
            });
          }}
          parentDdqlID={ddqlQuery.data.ddql.id}
          dealFirmGroupId={ddqlQuery.data.ddql.dealGroup?.id ?? ""}
          upload={false}
        />
        <AssignToDealGroupModal
          open={openModal === "assign_to_deal_group"}
          onClose={() => setOpenModal("")}
          ddqlQuery={ddqlQuery}
          dealId={activeDealId ?? ""}
        />

        <MoveQuestionToDDQLModal
          open={moveQuestionState.open}
          onClose={() =>
            setMoveQuestionState({
              open: false,
              question: null,
              targetDdqlId: null,
              targetDdqlName: null,
            })
          }
          question={moveQuestionState.question}
          targetDdqlId={moveQuestionState.targetDdqlId}
          targetDdqlName={moveQuestionState.targetDdqlName}
        />
      </div>
    </DdqlContext.Provider>
  );
}

// upload docx and pdf straight to server + start import task.
// preview excel import with diff deterministic parsers before giving option to try with AI.
function EmptyState(props: { dealId: string; ddql: DdqlQuery["ddql"] }) {
  const [dragActive, setDragActive] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const client = useGqlClient();
  const createDdqlImport = useCreateDdqlImportMutation(client);
  const { id: ddqlID } = useParams<{ id: string }>();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [openModal, setOpenModal] = useState<"" | "preview_ddql">("");

  const handleDrag = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleFileSelected = (file?: File | null) => {
    if (!file || !ddqlID || isUploading) return;
    setIsUploading(true);
    setUploadProgress(0);

    createDdqlImport.mutate(
      {
        ddqlID,
        fileName: file.name,
        fileType: file.type,
        parts: getNumberOfParts(file),
      },
      {
        onError: () => {
          setIsUploading(false);
        },
        onSuccess: (data) => {
          if (!data) {
            setIsUploading(false);
            return;
          }

          const response = data.createDdqlImport;

          const uploader = new Uploader({
            file,
            uploadId: response.multiPartUploadID,
            parts: response.parts.map((p) => ({
              PartNumber: p.partNumber,
              signedUrl: p.presignedUrl,
            })),
          });

          uploader.start();

          uploader.onProgress((progress) => {
            setUploadProgress(progress.percentage);
          });

          uploader.onError(() => {
            setIsUploading(false);
          });

          uploader.onComplete((uploadedParts) => {
            client
              .request<
                S3DdqlUploadCompleteMutation,
                S3DdqlUploadCompleteMutationVariables
              >(S3DdqlUploadCompleteDocument, {
                ddqlID: response.ddql.id,
                multiPartUploadID: response.multiPartUploadID,
                parts: uploadedParts.map((up) => ({
                  partNumber: up.PartNumber,
                  etag: up.ETag,
                })),
              })
              .finally(() => {
                queryClient.invalidateQueries({
                  queryKey: [
                    "Ddql",
                    { dealId: props.dealId, ddqlId: props.ddql.id },
                  ],
                });
                setIsUploading(false);
                setUploadProgress(0);
              });
          });
        },
      },
    );
  };

  const handleDrop = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (isUploading) return;
    const f: File | undefined = e.dataTransfer?.files?.[0];
    handleFileSelected(f);
  };

  if (props.ddql.status === DdqlStatus.Importing) {
    return (
      <div className="h-[calc(100vh-250px)] w-full flex flex-col items-center justify-center p-3">
        <div
          className={clsx(
            "flex flex-col items-center justify-center w-full h-full",
          )}
        >
          <div className="animate-pulse">
            <ImportIcon className="w-24 h-24 text-persian-800" />
          </div>

          <p className="text-xl font-bold text-persian-990 mt-4">
            Importing question list...
          </p>
          <p className="text-concrete-600 text-md">This can take 1-2 minutes</p>
        </div>
      </div>
    );
  }

  if (props.ddql.status === DdqlStatus.ReadyToImport) {
    return (
      <div className="h-[calc(100vh-250px)] w-full flex flex-col items-center justify-center p-3">
        <div
          className={clsx(
            "flex flex-col items-center justify-center w-full h-full",
          )}
        >
          <CheckCircleIcon className="w-24 h-24 text-persian-800" />
          <p className="text-xl font-bold text-persian-990 mt-4">
            Ready to import
          </p>
          <div className="flex items-center gap-x-2 mt-2 border border-concrete-200 bg-concrete-50 p-2 rounded-full">
            <LocalFileIcon
              fileName={props.ddql.sourceFileName}
              fileType={props.ddql.sourceFileType}
              size="s"
            />
            <p className="text-xs text-concrete-800">
              {props.ddql.sourceFileName}
            </p>
          </div>

          <Button
            onClick={() => {
              console.log("clicked");
              setOpenModal("preview_ddql");
            }}
            variant="positive"
            text="Import list"
            margin="l 0 0 0 "
          />
        </div>

        <PreviewDdqlModal
          ddql={props.ddql}
          sourceFilePresignedUrl={props.ddql.sourceFilePresignedUrl}
          open={openModal === "preview_ddql"}
          onClose={() => {
            setOpenModal("");
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="h-[calc(100vh-250px)] w-full flex flex-col items-center justify-center p-3"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {isUploading ? (
        <div className="flex flex-col items-center justify-center w-full h-full">
          <FileUpIcon className="w-24 h-24 text-persian-800" />
          <p className="text-xl font-bold text-persian-990 mt-4">
            Uploading file...
          </p>
          <div className="w-80 max-w-full mt-3">
            <ProgressBar
              progress={uploadProgress}
              height="h-1.5"
              backgroundColor="bg-persian-100"
              progressColor="bg-persian-600"
            />
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            "flex flex-col items-center justify-center w-full h-full",
            dragActive
              ? "bg-persian-50 border-2 rounded-md border-persian-200 border-dashed"
              : "",
          )}
        >
          <BadgeQuestionMarkIcon className="w-24 h-24 text-persian-800" />
          <p className="text-xl font-bold text-persian-990 mt-4">
            Ask a question, drag your file here or{" "}
            <button
              onClick={() => {
                if (inputFileRef.current) {
                  inputFileRef.current.click();
                }
              }}
              className="text-persian-800 hover:text-persian-500"
            >
              browse
            </button>
          </p>
          <p className="text-concrete-600 text-md">
            We support .xlsx, .docx and .pdf
          </p>
        </div>
      )}
      <input
        type="file"
        multiple={false}
        className="hidden"
        ref={inputFileRef}
        accept=".docx, .pdf, .xlsx"
        onChange={(e) => handleFileSelected(e.currentTarget.files?.[0] ?? null)}
        disabled={isUploading}
      />
    </div>
  );
}

function Header(props: { ddql: DdqlQuery["ddql"] }) {
  const ddql = props.ddql;
  const history = useHistory();
  const client = useGqlClient();
  const publishDdql = usePublishDdqlMutation(client);
  const queryClient = useQueryClient();
  const { invalidateDdql } = useInvalidateQueryKeys();
  const account = useSelector(authSelectors.account);

  const { setShowSourceFile, showSourceFile } = useContext(DdqlContext);

  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const [openModal, setOpenModal] = useState<
    "confirm_delete" | "manage_access" | "data_room_folder" | "source_file" | ""
  >("");

  const accessControlAccounts = [
    ...ddql.accessControlDealAccounts.map((da) => da.dealAccount.account),
    ...ddql.accessControlDealGroups.flatMap((acdg) =>
      acdg.accounts.map((da) => da.dealAccount.account),
    ),
  ];

  return (
    <div className="flex items-center justify-between w-full ">
      <div className="flex items-center gap-x-3">
        <BackButton size="xs" />
        <div className="flex flex-col justify-center">
          <p className="text-gray-800 font-bold text-md leading-tight">
            {ddql.name}
          </p>
        </div>
        <PermissionCheck
          input={{
            permissionKey: "questions:manage_access",
            resource: {
              resourceID: ddql.id,
              resourceType: PermissionsResourceType.Ddql,
            },
          }}
        >
          <div className="flex items-center ml-3 my-auto gap-x-3">
            {props.ddql.parentDdqls.length === 0 ? (
              <Avatars
                size="m"
                accounts={accessControlAccounts}
                max={9}
                showAdd={true}
                onClick={() => {
                  setOpenModal("manage_access");
                }}
              />
            ) : null}

            {/* <Button
              variant="neutral"
              size="xs"
              text="Manage access"
              icon={CogIcon}
              onClick={() => {
                setOpenModal("manage_access");
              }}
            /> */}
          </div>
        </PermissionCheck>
        {props.ddql.dataRoomFolder ? (
          <NavLink
            to={links.folderLink(props.ddql.dataRoomFolder.id, activeDealId)}
            className="flex items-center bg-concrete-50 rounded-full px-2 py-1 border border-concrete-100 gap-x-2 hover:border-concrete-200 hover:bg-concrete-100"
          >
            <p className="text-xs text-persian-990">Linked to </p>
            <div className="flex items-center gap-x-1">
              <FolderIcon className="w-4 h-4 text-blue-500" />{" "}
              <p className="text-xs font-semibold text-persian-990">
                {truncateText(
                  props.ddql.dataRoomFolder.name
                    ? props.ddql.dataRoomFolder.name
                    : "Home",
                  40,
                )}
              </p>
            </div>
          </NavLink>
        ) : null}
        {account &&
          account.id === props.ddql.createdBy.id &&
          props.ddql.sourceFileName ? (
          <div>
            <button
              onClick={() => {
                // setOpenModal("source_file");
                setShowSourceFile(!showSourceFile);
              }}
              className="flex items-center bg-concrete-50 rounded-full px-2 py-1 border border-concrete-100 gap-x-2 hover:border-concrete-200 hover:bg-concrete-100 cursor-pointer"
            >
              <LocalFileIcon
                fileType={props.ddql.sourceFileType}
                fileName={props.ddql.sourceFileName}
                size="s"
              />
              <p className="text-xs text-persian-990 font-semibold">
                {showSourceFile ? "Hide" : "View"} source file:{" "}
                {truncateText(props.ddql.sourceFileName, 40)}
              </p>
            </button>
            {/* <SourceFileSlideOver
              open={openModal === "source_file"}
              onClose={() => {
                setOpenModal("");
              }}
              sourceFileName={props.ddql.sourceFileName}
              sourceFileType={props.ddql.sourceFileType}
              presignedUrl={props.ddql.sourceFilePresignedUrl}
            /> */}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-x-2">
        <div className="flex items-center gap-x-1.5">
          <div className="w-32">
            <ProgressBar
              progress={
                (ddql.answeredChildQuestions / ddql.totalChildQuestions) * 100
              }
              height="h-1"
              progressColor={"bg-green-500"}
              backgroundColor={"bg-green-200/80"}
            />
          </div>
          <p className="text-xs text-gray-500 truncate">
            {ddql.answeredChildQuestions} / {ddql.totalChildQuestions}
          </p>
        </div>
        <PermissionCheck
          input={{
            permissionKey: "questions:manage_access",
            resource: {
              resourceID: ddql.id,
              resourceType: PermissionsResourceType.Ddql,
            },
          }}
        >
          <TripleDotMenu width="w-56">
            <TripleDotMenu.Button
              variant="normal"
              text="Link to Data Room folder"
              icon={<FolderIcon className="w-3 h-3 text-blue-500" />}
              onClick={() => {
                setOpenModal("data_room_folder");
              }}
            />
            <TripleDotMenu.Button
              variant="danger"
              text="Delete"
              icon={<TrashIcon className="w-3 h-3" />}
              onClick={() => {
                setOpenModal("confirm_delete");
              }}
            />
          </TripleDotMenu>
        </PermissionCheck>
      </div>

      <DeleteDDQLModal
        open={openModal === "confirm_delete"}
        onClose={() => {
          setOpenModal("");
        }}
        ddql={ddql}
      />

      <AddMoreModal
        open={openModal === "manage_access"}
        onClose={() => {
          setOpenModal("");
          invalidateDdql(ddql.id, activeDealId);
        }}
        dealId={activeDealId}
        resourceId={ddql.id}
        resourceType={PermissionsResourceType.Ddql}
        access={ddql.accessControlDealGroups as AccessControlDealGroup[]}
        title={`Manage access`}
      />

      <LinkDataRoomFolderModal
        open={openModal === "data_room_folder"}
        onClose={() => {
          setOpenModal("");
          invalidateDdql(ddql.id, activeDealId);
        }}
        ddql={ddql}
      />
    </div>
  );
}

function SourceFileSlideOver(props: {
  open: boolean;
  onClose: () => void;
  presignedUrl: string;
  sourceFileType: string;
  sourceFileName: string;
}) {
  const [showFile, setShowFile] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShowFile(true);
    }, 1000);
  }, [props.presignedUrl]);

  return (
    <SlideOver open={props.open} onClose={props.onClose} maxWidth="max-w-3xl">
      <div className="border-b border-gray-200 shadow-sm p-4 flex justify-between space-x-2">
        <div className="flex items-center space-x-2">
          <PanelRightCloseIcon
            onClick={() => props.onClose()}
            className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer"
          />
          <LocalFileIcon
            fileName={props.sourceFileName}
            fileType={props.sourceFileType}
          />
          <p className="text-sm text-gray-800 font-semibold">
            {props.sourceFileName}
          </p>
        </div>
      </div>
      <div className="h-[calc(100vh)] w-full p-2">
        <ErrorBoundary
          fallback={
            <div className="w-full h-full flex items-center justify-center">
              <p className="font-semibold text-sm text-concrete-600">
                Failed to load file
              </p>
            </div>
          }
        >
          {showFile ? (
            <div className="h-[calc(100vh-75px)] w-full">
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                  props.presignedUrl,
                )}`}
                className="w-full h-full"
              />
            </div>
          ) : null}
        </ErrorBoundary>
      </div>
    </SlideOver>
  );
}

function DdqlTable(props: {
  ddql: DdqlQuery["ddql"];
  filters: QuestionsFilters;
}) {
  const { ddql } = props;
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  return (
    <DdqlRow
      filters={props.filters}
      key={ddql.id}
      ddql={ddql}
      rootDdql={ddql}
      depth={0}
      onQuestionClick={(questionId) => {
        history.push(`/deal/${dealId}/questions/${questionId}`);
      }}
      expanded={true}
      renderHeader={false}
    />
  );
}

function DdqlActivity({
  ddqlId,
  dealId,
  onClose,
}: {
  ddqlId: string;
  dealId: string;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const activityQuery = useDealActivityQuery(client, {
    dealId: dealId,
    input: {
      count: ACTIVITY_PAGE_SIZE,
      cursor: page > 1 ? (page * ACTIVITY_PAGE_SIZE).toString() : "",
      ddqlID: ddqlId,
    },
  });

  const activities = activityQuery.data?.dealActivity?.activity || [];

  useEffect(() => {
    if (activityQuery.data) {
      setHasMore(activities.length === ACTIVITY_PAGE_SIZE);
    }
  }, [activityQuery.data, activities.length]);

  const filteredActivities = activities.filter((activity) =>
    DDQL_ACTIVITY_TYPES.includes(activity.type),
  );

  const hasItems = filteredActivities.length > 0;

  return (
    <div className="w-80 min-w-80 border-l border-concrete-100">
      <div className="flex items-center justify-between p-2 border-b border-concrete-100">
        <div className="flex items-center gap-x-2">
          <ActivityIcon className="w-4 h-4 text-gray-500" />
          <h5 className="text-md font-bold text-concrete-950/95 leading-none">
            Activity
          </h5>
        </div>
        <div className="flex items-center gap-x-2">
          <button
            className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
            disabled={page === 1}
            onClick={() => {
              if (page > 1) {
                setPage(page - 1);
              }
            }}
          >
            <ChevronDownIcon className="w-4 h-4 text-gray-500 rotate-90" />
          </button>

          <span className="text-sm text-gray-500">{page}</span>

          <button
            className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
            disabled={!hasMore}
            onClick={() => {
              if (hasMore) {
                setPage(page + 1);
              }
            }}
          >
            <ChevronDownIcon className="w-4 h-4 text-gray-500 -rotate-90" />
          </button>

          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <PanelRightIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>
      <div
        className={classNames("overflow-y-auto p-3", hasItems ? "flex-1" : "")}
      >
        {!hasItems && (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm">No activity yet</p>
          </div>
        )}

        <ul role="list" className="space-y-6">
          {filteredActivities.map((activity, i) => (
            <li
              key={activity.id}
              className="relative flex gap-x-4 items-center"
            >
              <div
                className={classNames(
                  i === filteredActivities.length - 1 ? "h-6" : "-bottom-6",
                  "absolute left-0 top-0 flex w-6 justify-center",
                )}
              >
                <div className="w-px bg-gray-200" />
              </div>

              <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                <GetDDQLActivityIcon type={activity.type} />
              </div>

              <div className="flex-auto">
                <div className="flex justify-between gap-x-4">
                  <div className="py-0.5 text-xs leading-5 text-gray-500">
                    <span className="font-medium text-gray-900">
                      {activity.account.name}
                    </span>{" "}
                    {activity.type === DealActivityType.QuestionAsked &&
                      "asked a question"}
                    {activity.type === DealActivityType.QuestionAnswered &&
                      "answered a question"}
                    {activity.type === DealActivityType.Comment &&
                      "commented on"}
                    {activity.type === DealActivityType.DdqlCreated &&
                      "created a list"}
                    {activity.type ===
                      DealActivityType.DdqlImportStatusFailed &&
                      "failed to import DDQL"}
                    {activity.type ===
                      DealActivityType.DdqlImportStatusSuccess &&
                      "successfully imported DDQL"}
                    {activity.type === DealActivityType.DdqlPermissionChanged &&
                      "changed permissions for DDQL"}
                    {activity.type === DealActivityType.DdqlPublished &&
                      "published DDQL"}
                    {activity.question && (
                      <>
                        {" "}
                        <NavLink
                          to={`/deal/${dealId}/questions/${activity.question.id}`}
                          className="font-semibold hover:text-gray-700"
                        >
                          {activity.question.title}
                        </NavLink>
                      </>
                    )}
                    {activity.type === DealActivityType.DdqlCreated &&
                      activity.ddql && (
                        <>
                          {" "}
                          <NavLink
                            to={`/deal/ddqls/${activity.ddql.id}`}
                            className="font-semibold hover:text-gray-700"
                          >
                            {activity.ddql.name}
                          </NavLink>
                        </>
                      )}
                  </div>
                  <time className="flex-none py-0.5 text-xs leading-5 text-gray-500">
                    {formatDistance(
                      secondsToMilliseconds(activity.createdAt),
                      new Date(),
                      {
                        addSuffix: true,
                      },
                    )}
                  </time>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function GetDDQLActivityIcon({ type }: { type: DealActivityType }) {
  if (type === DealActivityType.DdqlCreated) {
    return <ListBulletIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.DdqlImportStatusFailed) {
    return <ExclamationCircleIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.DdqlImportStatusSuccess) {
    return <CheckCircleIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.DdqlPermissionChanged) {
    return <LockClosedIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.DdqlPublished) {
    return <CheckCircleIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.QuestionAnswerApproved) {
    return <CheckCircleIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.Comment) {
    return <ChatBubbleLeftIcon className="w-4 h-4 text-gray-400" />;
  }
  if (type === DealActivityType.QuestionAnswered) {
    return <CheckCircleIcon className="w-4 h-4 text-gray-400" />;
  }

  return <CheckCircleIcon className="w-4 h-4 text-gray-400" />;
}

function AssignToDealGroupModal(props: {
  open: boolean;
  onClose: () => void;
  ddqlQuery: UseQueryResult<DdqlQuery, unknown>;
  dealId: string;
}) {
  const [selectedDealGroup, setSelectedDealGroup] = useState<string | null>(
    props.ddqlQuery.data?.ddql.forDealGroup?.id ?? null,
  );

  useEffect(() => {
    setSelectedDealGroup(props.ddqlQuery.data?.ddql.forDealGroup?.id ?? null);
  }, [props.ddqlQuery.data, props.open]);

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateDdql = useUpdateDdqlMutation(client);

  function onClose() {
    setSelectedDealGroup(null);
    props.onClose();
  }

  return (
    <AnimatedModal open={props.open} onClose={onClose} size="lg">
      <div className="flex flex-col gap-y-4">
        <div className="">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Assign to Deal Group
            </h3>
            <CloseIcon onClose={onClose} />
          </div>
          <div className="space-y-3">
            {props.ddqlQuery.data?.deal.guestGroups.map((group) => (
              <label
                key={group.id}
                className="flex items-center space-x-3 px-2 py-2 hover:bg-gray-50 cursor-pointer rounded-md transition-colors duration-150"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={selectedDealGroup === group.id}
                  onChange={() =>
                    setSelectedDealGroup(
                      selectedDealGroup === group.id ? null : group.id,
                    )
                  }
                />
                <span className="text-md font-semibold text-gray-700">
                  {group.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({group.accounts.length}{" "}
                  {group.accounts.length === 1 ? "member" : "members"})
                </span>
              </label>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-x-3">
            <Button variant="neutral" text="Cancel" onClick={props.onClose} />
            <Button
              isLoading={updateDdql.isPending}
              variant="positive"
              text="Assign"
              onClick={() => {
                if (!props.ddqlQuery.data || !selectedDealGroup) {
                  return;
                }

                updateDdql.mutate(
                  {
                    input: {
                      id: props.ddqlQuery.data.ddql.id,
                      forDealGroupID: selectedDealGroup,
                    },
                  },
                  {
                    onSuccess: () => {
                      toasts.success(`DDQL assigned`);
                      onClose();
                      if (props.ddqlQuery.data) {
                        queryClient.invalidateQueries({
                          queryKey: [
                            "Ddql",
                            {
                              id: props.ddqlQuery.data.ddql.id,
                              dealId: props.dealId,
                            },
                          ],
                        });
                      }
                    },
                    onError: () => {
                      toasts.error(`Failed to assign DDQL`);
                    },
                  },
                );
              }}
            />
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}

function SetQuestionsPriority(props: {
  ddql: DdqlQuery["ddql"];
  onComplete: () => void;
}) {
  const { selectedQuestions, setSelectedQuestions } = useContext(DdqlContext);
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateQuestions = useUpdateQuestionsMutation(client);

  const options = [
    QuestionPriority.None,
    QuestionPriority.Low,
    QuestionPriority.Medium,
    QuestionPriority.High,
    QuestionPriority.Urgent,
  ];

  return (
    <div>
      {options.map((option) => {
        return (
          <div
            className="flex items-center gap-x-2 p-2 hover:bg-persian-50 cursor-pointer"
            onClick={() => {
              toasts.info("Updating questions...");
              updateQuestions.mutate(
                {
                  input: selectedQuestions.map((q) => {
                    return {
                      id: q,
                      priority: option,
                    };
                  }),
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql", { id: props.ddql.id }],
                    });
                    setSelectedQuestions([]);
                    props.onComplete();
                    toasts.success("Questions updated");
                  },
                },
              );
            }}
          >
            <QuestionPriorityIcon priority={option} />
            <p className="text-sm text-persian-990">
              {getQuestionPriorityText(option)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function MoveQuestions(props: {
  ddql: DdqlQuery["ddql"];
  onComplete: () => void;
}) {
  const [selectedDdql, setSelectedDdql] = useState("");
  const { selectedQuestions, setSelectedQuestions } = useContext(DdqlContext);
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateQuestions = useUpdateQuestionsMutation(client);

  return (
    <div>
      <div className="max-h-60 overflow-y-auto">
        {props.ddql.children.map((ddql) => {
          return (
            <MoveQuestionDdqlItem
              selected={selectedDdql}
              onSelected={(id) => {
                if (id === selectedDdql) {
                  setSelectedDdql("");
                } else {
                  setSelectedDdql(id);
                }
              }}
              ddql={ddql as GqlDdql}
              depth={0}
            />
          );
        })}
      </div>

      <div className="flex justify-end p-2 border-t border-concretee-100">
        <Button
          isDisabled={selectedDdql === ""}
          text="Move"
          variant="positive"
          isLoading={updateQuestions.isPending}
          onClick={() => {
            if (updateQuestions.isPending) {
              return;
            }

            updateQuestions.mutate(
              {
                input: selectedQuestions.map((q) => {
                  return { id: q, ddqlID: selectedDdql };
                }),
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["Ddql", { id: props.ddql.id }],
                  });
                  setSelectedQuestions([]);
                  props.onComplete();
                  toasts.success("Questions moved");
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}

function MoveQuestionDdqlItem(props: {
  ddql: DdqlQuery["ddql"];
  depth: number;
  onSelected: (id: string) => void;
  selected: string;
}) {
  const paddingLeft = props.depth > 0 ? `${props.depth * 0.8}rem` : "";
  return (
    <div>
      <div
        onClick={() => {
          props.onSelected(props.ddql.id);
        }}
        className={clsx(
          "py-2 hover:bg-persian-50 px-2 cursor-pointer flex gap-x-2",
        )}
        style={{
          paddingLeft: paddingLeft,
        }}
      >
        <Checkbox
          checked={props.ddql.id === props.selected}
          onChange={() => {
            props.onSelected(props.ddql.id);
          }}
        />
        <p className="text-sm text-persian-990 truncate select-none">
          {props.ddql.name}
        </p>
      </div>
      {props.ddql.children.map((ddql) => (
        <MoveQuestionDdqlItem
          ddql={ddql as GqlDdql}
          depth={props.depth + 1}
          selected={props.selected}
          onSelected={props.onSelected}
        />
      ))}
    </div>
  );
}

interface DDQLNodeProps {
  ddql: GqlDdql;
  depth?: number;
  isLast?: boolean;
  rootDdqlStatus: DdqlStatus;
  parentDdqls?: Array<{ id: string; name: string }>;
  currentlyDraggedQuestionDdqlId?: string | null;
  canDragAndDrop?: boolean;
}

function DDQLNode({
  ddql,
  depth = 0,
  isLast = false,
  rootDdqlStatus,
  parentDdqls = [],
  currentlyDraggedQuestionDdqlId = null,
  canDragAndDrop = false,
}: DDQLNodeProps) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const marginClasses = ["ml-0", "ml-4", "ml-8", "ml-12", "ml-16", "ml-20"];

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showNewQuestionModal, setShowNewQuestionModal] = useState(false);

  const [showNewDDQLModal, setShowNewDDQLModal] = useState(false);

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const deleteDdql = useDeleteDdqlMutation(client);

  const hasEmptyName = !ddql.name || ddql.name.trim() === "";
  const hasQuestions = ddql.questions?.length > 0;
  const hasChildren = ddql.children?.length > 0;

  const currentNodeWithParents = [
    ...parentDdqls,
    { id: ddql.id, name: ddql.name || "Unnamed List" },
  ];

  const parentDdqlId =
    parentDdqls.length > 0 ? parentDdqls[parentDdqls.length - 1].id : undefined;

  if (hasEmptyName && hasQuestions) {
    return (
      <div
        className={classNames("flex flex-col", depth === 0 ? "mt-4" : "mt-2")}
      >
        <div
          className={classNames(
            "border-l-2 border-gray-100",
            depth > 0 ? marginClasses[depth] : "ml-6",
          )}
        >
          {!hasChildren && (
            <Droppable
              droppableId={`ddql-${ddql.id}`}
              isDropDisabled={
                hasChildren || ddql.id === currentlyDraggedQuestionDdqlId
              }
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classNames(
                    "pl-6 py-2",
                    snapshot.isDraggingOver &&
                      !hasChildren &&
                      ddql.id !== currentlyDraggedQuestionDdqlId
                      ? "bg-indigo-50/60 rounded-md"
                      : "",
                  )}
                >
                  {ddql.questions
                    ?.sort((a, b) =>
                      (a.index || "").localeCompare(b.index || ""),
                    )
                    .map((q, qIndex) => (
                      <Draggable
                        key={q.id}
                        draggableId={`question-${q.id}`}
                        index={qIndex}
                        isDragDisabled={!canDragAndDrop}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={classNames(
                              snapshot.isDragging ? "opacity-60" : "",
                            )}
                          >
                            <Question
                              key={q.id}
                              isFirst={qIndex === 0}
                              isLast={
                                qIndex === (ddql.questions?.length || 0) - 1
                              }
                              question={q as GqlQuestion}
                              showComments={false}
                              showStatus={true}
                              showAvatar={false}
                              showIndex={false}
                              showAssignToDealGroup={false}
                              parentDdqls={currentNodeWithParents}
                              showPriority={true}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {/* Render children if any */}
          {hasChildren && (
            <div
              className={classNames("space-y-1", hasQuestions ? "mt-2" : "")}
            >
              {ddql.children?.map((childDdql, index) => (
                <DDQLNode
                  key={childDdql.id}
                  ddql={childDdql}
                  depth={depth + 1}
                  isLast={index === (ddql.children?.length || 0) - 1}
                  rootDdqlStatus={rootDdqlStatus}
                  parentDdqls={currentNodeWithParents}
                  currentlyDraggedQuestionDdqlId={
                    currentlyDraggedQuestionDdqlId
                  }
                  canDragAndDrop={canDragAndDrop}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // For empty-named DDQLs without questions
  if (hasEmptyName && !hasQuestions) {
    // Flag to determine if we should show "No questions" message
    const showNoQuestionsMessage = !hasChildren;

    return (
      <div
        className={classNames("flex flex-col", depth === 0 ? "mt-4" : "mt-2")}
      >
        <div
          className={classNames(
            "border-l-2 border-gray-100",
            depth > 0 ? marginClasses[depth] : "ml-6",
          )}
        >
          {!hasChildren && (
            <Droppable
              droppableId={`ddql-${ddql.id}`}
              isDropDisabled={
                hasChildren || ddql.id === currentlyDraggedQuestionDdqlId
              }
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classNames(
                    "pl-6 min-h-[40px] flex items-center justify-center",
                    snapshot.isDraggingOver &&
                      !hasChildren &&
                      ddql.id !== currentlyDraggedQuestionDdqlId
                      ? "bg-indigo-50/80 rounded-md border border-dashed border-indigo-300"
                      : "border border-dashed border-transparent",
                  )}
                >
                  {!snapshot.isDraggingOver && showNoQuestionsMessage && (
                    <p className="text-xs text-gray-400">No questions</p>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {hasChildren && (
            <div className="space-y-1">
              {ddql.children?.map((childDdql, index) => (
                <DDQLNode
                  key={childDdql.id}
                  ddql={childDdql}
                  depth={depth + 1}
                  isLast={index === (ddql.children?.length || 0) - 1}
                  rootDdqlStatus={rootDdqlStatus}
                  parentDdqls={currentNodeWithParents}
                  currentlyDraggedQuestionDdqlId={
                    currentlyDraggedQuestionDdqlId
                  }
                  canDragAndDrop={canDragAndDrop}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const canReceiveDrops =
    canDragAndDrop &&
    !hasChildren &&
    ddql.id !== currentlyDraggedQuestionDdqlId;

  return (
    <div className={classNames("flex flex-col", depth === 0 ? "mt-4" : "mt-2")}>
      <div
        className={classNames(
          "flex items-center justify-between rounded-md transition-colors duration-150",
          "hover:bg-indigo-50/70 group px-4 py-2.5",
          depth > 0 ? marginClasses[depth] : "",
          "border-l-2",
        )}
        onClick={(e) => {
          if (depth > 0) {
            e.preventDefault();
            setIsCollapsed(!isCollapsed);
          }
        }}
      >
        <div className="flex items-center gap-x-2">
          {depth > 0 && (
            <ChevronRightIcon
              className={classNames(
                "w-4 h-4 text-gray-400 transition-transform",
                isCollapsed ? "" : "rotate-90",
              )}
            />
          )}
          <div
            className={classNames(
              "rounded-full",
              depth === 0 ? "w-2 h-2" : "w-1.5 h-1.5",
            )}
          />
          <p
            className={classNames(
              "group-hover:text-indigo-600 transition-colors duration-150",
              depth === 0
                ? "text-base font-semibold text-gray-700"
                : "text-sm font-semibold text-gray-600",
            )}
          >
            {ddql.name}
          </p>
        </div>

        <div className="flex items-center gap-x-3 text-xs text-gray-500 font-medium min-w-[200px] justify-end">
          {hasChildren && (
            <>
              <span>{ddql.children?.length || 0} sublists</span>
            </>
          )}
          {!hasChildren && (
            <>
              <ProgressBar
                progress={
                  ddql.questions?.length === 0
                    ? 0
                    : (ddql.questions?.filter(
                      (q) => q.status === QuestionStatus.Approved,
                    ).length /
                      ddql.questions?.length) *
                    100
                }
              />
              <p className="text-xs text-gray-500/80 whitespace-nowrap">
                {
                  ddql.questions?.filter(
                    (q) => q.status === QuestionStatus.Approved,
                  ).length
                }
                /{ddql.questions?.length}
              </p>
            </>
          )}
          <PermissionCheck
            input={{
              permissionKey: "questions:edit",
              resource: {
                resourceID: ddql.id,
                resourceType: PermissionsResourceType.Ddql,
              },
            }}
          >
            <TripleDotMenu width="w-44">
              <Menu.Item>
                {({ active, close }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-50 text-indigo-700" : "",
                      "w-full block px-3 py-1 text-sm leading-6 text-indigo-600 text-left cursor-pointer",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowNewQuestionModal(true);
                      close();
                    }}
                  >
                    Add question
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active, close }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-50 text-indigo-700" : "",
                      "w-full block px-3 py-1 text-sm leading-6 text-indigo-600 text-left cursor-pointer",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowNewDDQLModal(true);
                      close();
                    }}
                  >
                    Add sublist
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active, close }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-50 text-red-700" : "",
                      "w-full block px-3 py-1 text-sm leading-6 text-red-600 text-left cursor-pointer",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowDeleteModal(true);
                      close();
                    }}
                  >
                    Delete sublist
                  </div>
                )}
              </Menu.Item>
            </TripleDotMenu>
          </PermissionCheck>
        </div>
      </div>

      <DeleteDDQLModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        ddql={ddql}
        parentDdqlId={parentDdqlId}
      />

      <NewQuestionModal
        open={showNewQuestionModal}
        onClose={() => {
          setShowNewQuestionModal(false);
          if (activeDealId) {
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id: ddql.id, dealId: activeDealId }],
            });

            if (parentDdqlId) {
              queryClient.invalidateQueries({
                queryKey: ["Ddql", { id: parentDdqlId, dealId: activeDealId }],
              });
            }
          }

          queryClient.invalidateQueries({
            queryKey: ["Ddql", { id: ddql.id }],
          });

          if (parentDdqlId) {
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id: parentDdqlId }],
            });
          }

          queryClient.invalidateQueries({
            queryKey: ["Ddql"],
          });
        }}
        ddqlId={ddql.id}
        parentDdqlId={parentDdqlId}
      />

      <NewDDQLModal
        open={showNewDDQLModal}
        redirectOnSuccess={false}
        onClose={() => {
          setShowNewDDQLModal(false);
          queryClient.invalidateQueries({
            queryKey: ["Ddql"],
          });
        }}
        parentDdqlID={ddql.id}
        dealFirmGroupId={ddql.dealGroup?.id ?? ""}
        upload={false}
      />

      {!isCollapsed && (
        <div
          className={classNames(
            "border-l-2 border-gray-100",
            depth > 0 ? marginClasses[depth] : "ml-6",
          )}
        >
          {hasQuestions && (
            <Droppable
              droppableId={`ddql-${ddql.id}`}
              isDropDisabled={!canReceiveDrops}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classNames(
                    "pl-6 py-2",
                    snapshot.isDraggingOver && canReceiveDrops
                      ? "bg-indigo-50/60 rounded-md"
                      : "",
                  )}
                >
                  {ddql.questions
                    ?.sort((a, b) =>
                      (a.index || "").localeCompare(b.index || ""),
                    )
                    .map((q, qIndex) => (
                      <Draggable
                        key={q.id}
                        draggableId={`question-${q.id}`}
                        index={qIndex}
                        isDragDisabled={!canDragAndDrop}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={classNames(
                              snapshot.isDragging ? "opacity-60" : "",
                            )}
                          >
                            <Question
                              key={q.id}
                              isFirst={qIndex === 0}
                              isLast={
                                qIndex === (ddql.questions?.length || 0) - 1
                              }
                              question={q as GqlQuestion}
                              showComments={false}
                              showStatus={true}
                              showAvatar={false}
                              showIndex={false}
                              showAssignToDealGroup={false}
                              parentDdqls={currentNodeWithParents}
                              showPriority={true}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {!hasChildren && !hasQuestions && (
            <Droppable
              droppableId={`ddql-${ddql.id}`}
              isDropDisabled={!canReceiveDrops}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classNames(
                    "pl-6 min-h-[40px] flex items-center justify-center",
                    snapshot.isDraggingOver && canReceiveDrops
                      ? "bg-indigo-50/80 rounded-md"
                      : "border border-dashed border-transparent",
                  )}
                >
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          <div className={classNames("space-y-1", hasQuestions ? "mt-2" : "")}>
            {ddql?.children?.map((childDdql, index) => (
              <DDQLNode
                key={childDdql.id}
                ddql={childDdql}
                depth={depth + 1}
                isLast={index === (ddql.children?.length || 0) - 1}
                rootDdqlStatus={rootDdqlStatus}
                parentDdqls={currentNodeWithParents}
                currentlyDraggedQuestionDdqlId={currentlyDraggedQuestionDdqlId}
                canDragAndDrop={canDragAndDrop}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
